import { reactive } from "vue";

import { executeTask, getTaskExecution, listTaskExecutions } from "../api/plans.js";
import { refreshNotifications } from "./notifications.js";

const OPEN_STATES = new Set(["queued", "running"]);
const TERMINAL_STATES = new Set(["done", "failed"]);
const POLL_INTERVAL_MS = 2_500;
const DISCOVERY_INTERVALS = 4;

// Plan execution tracking belongs to the app, not to a view.  The server owns the durable job;
// this store only keeps the small reactive projection needed by Plan and Maya while they are open.
export const planExecutions = reactive({
  loading: false,
  error: "",
  jobs: {},
  taskJobs: {},
  failures: {},
});

let trackerStarted = false;
let pollTimer = null;
let pollInFlight = false;
let discoveryInFlight = null;
let pollCount = 0;
const pollingJobs = new Set();
const pendingSubmissions = new Map();
const completedJobs = new Set();
const completionListeners = new Set();

function asId(value) {
  if (value == null || value === "") return null;
  return String(value);
}

function asTaskKey(value) {
  return asId(value);
}

function normalizeState(value, fallback = "") {
  const result = String(value || fallback || "").trim().toLowerCase();
  return OPEN_STATES.has(result) || TERMINAL_STATES.has(result) ? result : "";
}

function normalizeExecution(payload, fallback = {}) {
  const raw = payload && typeof payload === "object" && !Array.isArray(payload)
    ? payload
    : {};
  const source = raw.execution && typeof raw.execution === "object" ? raw.execution : raw;
  const job = asId(source.job_id ?? source.jobId ?? source.job ?? fallback.job_id ?? fallback.jobId ?? fallback.job);
  const taskId = source.task_id ?? source.taskId ?? fallback.task_id ?? fallback.taskId;
  const executionState = normalizeState(source.state ?? source.status, fallback.state);
  if (!job || taskId == null || !executionState) return null;
  return {
    ...fallback,
    ...source,
    job,
    job_id: job,
    taskId,
    task_id: taskId,
    state: executionState,
  };
}

function executionRecords(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of ["executions", "jobs", "items", "data"]) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
}

function rememberOpen(execution) {
  const task = asTaskKey(execution.taskId);
  const job = execution.job;
  if (!task || !job) return null;
  const oldJob = planExecutions.taskJobs[task];
  if (oldJob && oldJob !== job) delete planExecutions.jobs[oldJob];
  const previous = planExecutions.jobs[job];
  planExecutions.jobs[job] = {
    ...previous,
    ...execution,
    observedAt: Date.now(),
    submittedAt: previous?.submittedAt || Date.now(),
  };
  planExecutions.taskJobs[task] = job;
  delete planExecutions.failures[task];
  // Retries reuse the durable public job id, so a retried execution may emit completion again.
  completedJobs.delete(job);
  return planExecutions.jobs[job];
}

function removeOpen(execution) {
  const job = asId(execution?.job ?? execution?.job_id);
  const stored = job ? planExecutions.jobs[job] : null;
  const task = asTaskKey(execution?.taskId ?? execution?.task_id ?? stored?.taskId);
  if (job) delete planExecutions.jobs[job];
  if (task && planExecutions.taskJobs[task] === job) delete planExecutions.taskJobs[task];
  return stored || execution;
}

function emitCompleted(execution) {
  const event = {
    ...execution,
    job: execution.job,
    job_id: execution.job,
    taskId: execution.taskId,
    task_id: execution.taskId,
    state: "done",
  };
  for (const listener of completionListeners) {
    try {
      listener(event);
    } catch {
      // A view subscriber must not prevent notification refresh or other subscribers.
    }
  }
  if (typeof globalThis.dispatchEvent === "function" && typeof globalThis.CustomEvent === "function") {
    globalThis.dispatchEvent(new globalThis.CustomEvent("soa:plan-execution-completed", { detail: event }));
  }
}

async function settle(execution) {
  const job = execution.job;
  const task = asTaskKey(execution.taskId);
  if (execution.state === "done") {
    const stored = removeOpen(execution);
    if (task) delete planExecutions.failures[task];
    if (completedJobs.has(job)) return execution;
    completedJobs.add(job);
    await refreshNotifications().catch(() => {});
    emitCompleted({ ...stored, ...execution });
    return execution;
  }
  if (execution.state === "failed") {
    const stored = removeOpen(execution);
    if (task) planExecutions.failures[task] = { ...stored, ...execution, observedAt: Date.now() };
  }
  return execution;
}

async function applyExecution(payload, fallback = {}) {
  const execution = normalizeExecution(payload, fallback);
  if (!execution) return null;
  return OPEN_STATES.has(execution.state) ? rememberOpen(execution) : settle(execution);
}

export function planExecutionForTask(taskId) {
  const task = asTaskKey(taskId);
  if (!task) return null;
  const job = planExecutions.taskJobs[task];
  return job ? planExecutions.jobs[job] || null : null;
}

export function planExecutionFailureForTask(taskId) {
  const task = asTaskKey(taskId);
  return task ? planExecutions.failures[task] || null : null;
}

export function isPlanExecutionActive(taskId) {
  return Boolean(planExecutionForTask(taskId));
}

export function onPlanExecutionCompleted(listener) {
  if (typeof listener !== "function") throw new TypeError("Plan execution completion listener must be a function");
  completionListeners.add(listener);
  return () => completionListeners.delete(listener);
}

export function refreshOpenPlanExecutions() {
  if (discoveryInFlight) return discoveryInFlight;
  planExecutions.loading = true;
  planExecutions.error = "";
  discoveryInFlight = listTaskExecutions()
    .then(async (payload) => {
      const records = executionRecords(payload);
      for (const record of records) await applyExecution(record);
      return records;
    })
    .catch((error) => {
      planExecutions.error = error.message || "Plan executions are unavailable.";
      throw error;
    })
    .finally(() => {
      planExecutions.loading = false;
      discoveryInFlight = null;
    });
  return discoveryInFlight;
}

async function pollOne(execution) {
  const job = execution.job;
  if (!job || pollingJobs.has(job)) return;
  pollingJobs.add(job);
  try {
    const result = await getTaskExecution(job);
    await applyExecution(result, execution);
  } catch (error) {
    if (error?.status === 404) await applyExecution({ state: "failed", reason: "not_found" }, execution);
    else planExecutions.error = error.message || "Plan execution status is unavailable.";
  } finally {
    pollingJobs.delete(job);
  }
}

export async function pollPlanExecutions() {
  if (pollInFlight) return;
  pollInFlight = true;
  try {
    await Promise.all(Object.values(planExecutions.jobs).map((execution) => pollOne(execution)));
    pollCount += 1;
    if (pollCount % DISCOVERY_INTERVALS === 0) await refreshOpenPlanExecutions().catch(() => {});
  } finally {
    pollInFlight = false;
  }
}

export function submitPlanExecution(taskId, { signal } = {}) {
  const task = asTaskKey(taskId);
  if (!task) return Promise.reject(new TypeError("taskId is required"));
  if (pendingSubmissions.has(task)) return pendingSubmissions.get(task);
  planExecutions.error = "";
  const submission = executeTask(taskId, { signal })
    .then(async (result) => {
      const execution = await applyExecution(result, { taskId, task_id: taskId });
      if (!execution) throw new Error("Plan execution did not return a job id");
      void pollPlanExecutions();
      return { ...result, job: execution.job, state: execution.state };
    })
    .catch((error) => {
      planExecutions.error = error.message || "Unable to start task.";
      throw error;
    })
    .finally(() => pendingSubmissions.delete(task));
  pendingSubmissions.set(task, submission);
  return submission;
}

export async function startPlanExecutionTracker() {
  if (trackerStarted) return;
  trackerStarted = true;
  await refreshOpenPlanExecutions().catch(() => {});
  void pollPlanExecutions();
  pollTimer = globalThis.setInterval(() => { void pollPlanExecutions(); }, POLL_INTERVAL_MS);
}

export function stopPlanExecutionTracker() {
  trackerStarted = false;
  if (pollTimer != null) globalThis.clearInterval(pollTimer);
  pollTimer = null;
}
